import { RouterOutput } from "@mindlercare/mindlerapi-client";
import { Icon, Menu, Render } from "@mindlercare/ui-web";
import { Typography } from "components/typography";
import CONSTANTS from "core/constants";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useImplementedFeature } from "../../../../core/selectors/app";
import { Slot } from "../../../../core/types";
import { getTemplate } from "../../../../helpers/templates";
import { useTemplates } from "../../../../hooks/useTemplates";
import { usePatientUserIdFromUrl } from "hooks/usePatientUserIdFromUrl";
import { useSelectedTemplate } from "hooks/useSelectedTemplate";

type Patient = Exclude<RouterOutput["patients"]["getByUserId"], null>;

export const SelectActionMenu = ({
  slot,
  patient,
  onOpenAssignModal,
  onClose,
}: {
  slot: Slot;
  patient: Patient;
  onOpenAssignModal: () => void;
  onClose: () => void;
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [, setSelectedTemplate] = useSelectedTemplate();
  const [patientUserIdFromSearchParams, setPatientUserId] =
    usePatientUserIdFromUrl();
  const [isMessagingEnabled] = useImplementedFeature("MESSAGING_TO_PATIENT");
  const { data: templates } = useTemplates();
  const slotTemplate = getTemplate(slot.templateId, templates);
  const showAssignIcbt = slot.status !== CONSTANTS.BOOKING_STATUS.OPEN;
  const showBookNewMeeting =
    !!slotTemplate &&
    Number(patientUserIdFromSearchParams) !== patient.userId &&
    slot.status !== "OPEN";
  const showActionMenu = showAssignIcbt || showBookNewMeeting; // Only show if there are actions available

  return (
    <Render if={showActionMenu}>
      <Menu>
        <Menu.Trigger variant="secondary-small" testId="selectAction">
          <Icon.Add />
          <Typography variant="body-small">{t("SELECT_ACTION")}</Typography>
          <Icon.Expand />
        </Menu.Trigger>
        <Menu.Items>
          <Render if={showAssignIcbt}>
            <Menu.Item onClick={onOpenAssignModal}>
              <Typography variant="body-small">{t("ASSIGN_ICBT")}</Typography>
            </Menu.Item>
          </Render>
          <Render if={showBookNewMeeting}>
            <Menu.Item
              onClick={async () => {
                if (!patient?.userId) return;
                await setSelectedTemplate(slotTemplate);
                await setPatientUserId(patient.userId.toString());
                onClose();
              }}
            >
              <Typography variant="body-small">
                {t("BOOK_NEW_MEETING")}
              </Typography>
            </Menu.Item>
          </Render>
          <Render if={isMessagingEnabled}>
            <Menu.Item
              onClick={() => {
                onClose();
                navigate(`/messages/${patient.userId}/new`);
              }}
            >
              <Typography variant="body-small">
                {t("SEND_MESSAGE_MODAL")}
              </Typography>
            </Menu.Item>
          </Render>
        </Menu.Items>
      </Menu>
    </Render>
  );
};
